import React, { useState } from "react";
import { PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/lib/useSettings";
import { Mail, Phone, MapPin } from "lucide-react";

export default function Contact() {
    const { data: settings } = useSettings();
    const s = settings || {};
    const [form, setForm] = useState({ name: "", mobile: "", email: "", subject: "", message: "" });
    const [loading, setLoading] = useState(false);

    const update = (k, v) => setForm({ ...form, [k]: v });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contacts", form);
            toast.success("Message sent. We'll get back to you within 2 business days.");
            setForm({ name: "", mobile: "", email: "", subject: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not send");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <PageHeader
                eyebrow="Get in touch"
                title={<>Let's <span className="text-secondary italic font-light">talk.</span></>}
                sub="For partnerships, queries, donation receipts or volunteering — drop us a note. We read every message."
            />

            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5 space-y-6">
                    <div className="bg-card border border-border p-6">
                        <div className="font-eyebrow text-xs text-secondary mb-4">Reach out</div>
                        <ul className="space-y-4 text-sm">
                            <li className="flex gap-3"><MapPin className="h-4 w-4 mt-1 text-secondary shrink-0" /><span>{s.contact_address}</span></li>
                            <li className="flex gap-3"><Phone className="h-4 w-4 mt-1 text-secondary shrink-0" /><a href={`tel:${s.contact_phone}`} className="hover:text-primary">{s.contact_phone}</a></li>
                            <li className="flex gap-3"><Mail className="h-4 w-4 mt-1 text-secondary shrink-0" /><a href={`mailto:${s.contact_email}`} className="hover:text-primary">{s.contact_email}</a></li>
                        </ul>
                    </div>
                    <div className="aspect-[4/3] bg-muted border border-border overflow-hidden">
                        <iframe
                            title="Tishya Foundation Location"
                            src={s.google_maps_embed}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <form onSubmit={submit} data-testid="contact-form" className="bg-card border border-border p-6 lg:p-10 space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <Label htmlFor="c-name" className="font-eyebrow text-xs">Your name</Label>
                                <Input id="c-name" data-testid="contact-name" required value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1.5" />
                            </div>
                            <div>
                                <Label htmlFor="c-mobile" className="font-eyebrow text-xs">Mobile (optional)</Label>
                                <Input id="c-mobile" data-testid="contact-mobile" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} className="mt-1.5" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="c-email" className="font-eyebrow text-xs">Email</Label>
                            <Input id="c-email" type="email" data-testid="contact-email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label htmlFor="c-subject" className="font-eyebrow text-xs">Subject</Label>
                            <Input id="c-subject" data-testid="contact-subject" required value={form.subject} onChange={(e) => update("subject", e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label htmlFor="c-message" className="font-eyebrow text-xs">Message</Label>
                            <Textarea id="c-message" data-testid="contact-message" required value={form.message} onChange={(e) => update("message", e.target.value)} rows={6} className="mt-1.5" />
                        </div>
                        <Button data-testid="contact-submit" type="submit" disabled={loading} className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8">
                            {loading ? "Sending…" : "Send message"}
                        </Button>
                    </form>
                </div>
            </section>
        </div>
    );
}
